function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/**
 * Multi metrics menu: clickable list of metrics(events, profile, funnels, retention, formula, cohorts) to go inside mp-drop-menu
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useDomEvent } from 'common/hooks/useDomEvent';
import { useCohorts } from 'common/contexts/react/cohorts-context';
import { ButtonTheme } from 'common/components/button/constants';
import { useDataGroups } from 'common/contexts/react/data-group-context';
import { useEventsController } from 'common/contexts/react/events-controller-context';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { PositioningStrategy } from 'common/components/tooltip/types';
import { Directions } from 'common/components/drop-menu/types';
import { ContextPanelData } from 'common/widgets/context-panel/types';
import { ContextPanelReact } from 'common/widgets/context-panel';
import { emitTrackEvent } from 'common/util/dom';
import { TrackingUsageContext } from 'common/report/tracking-setup';
import { promptCustomEvents } from 'common/widgets/custom-event-modal/utils';
import { userCookieUtils } from 'common/report/util/user-identity-from-cookie';
import { userDetailsStore } from 'common/report/stores/user-details';
import { Cohort as CohortModel } from 'common/query-builder/cohorts-query-builder/models/cohort';
import { promptInlineCohorts } from 'common/widgets/inline-cohorts-modal/util';
import { ResourceType } from 'common/report/constants';
import { useTranslation } from 'common/i18n/hooks';
import { SerializedCohort } from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import { NewQueryBlock } from '../new-query-block';
import { SelectorType } from '../constants';
import { getCustomEventMenuItem } from '../contexts/events-controller';
import { commitCohort } from '../properties-menu/util';
import { convertDataGroupIdToSet } from '../utils/data-group-util';
import { EventItem } from '../events-menu/types';
import { InlineCohortSegment } from '../segment-menu/types';
import { MultiMetricsMenu } from './index';
import { CohortItemValue, CohortSectionItem, EventSectionItem, isCohortMenuSectionItem, MenuType } from './types';
import { useSelectedLabel } from './hooks';
import { createContextPanelEventHandlers, createInlineCohortModalAttrs, getTooltipData } from './utils';
import { TrackingEvent } from './tracking';
type Props = Pick<React.ComponentProps<typeof MultiMetricsMenu>, `selected` | `allDataGroups` | `dataGroupId` | `metricLevelDataGroups` | `descriptionEditDisabled` | `excludeAutotrackEvents` | `excludeCustomEvents` | `excludedMetricsRowTypes` | `hideEditControls` | `projectHasPeople` | `projectHasCohorts` | `showAllEvents` | `showAnyEvent` | `showSessionEvent` | `showSpecialDatasetProperty` | `showTopEvents` | `supportB2bCohorts` | `queryOrigin` | `trackName` | `projectId` | `workspaceId` | `userId` | `onSelect` | `onCreate` | `onTrack`> & Pick<React.ComponentProps<typeof NewQueryBlock>, `className` | `elref` | `open` | `leftIcon` | `buttonSize` | `theme` | `readonly`> & {
  contextPanelParentElement?: HTMLElement | null;
  onOpen?: () => void;
  onClose?: () => void;
  onRemove?: () => void;
};
export function MultiMetricsQueryBlock(props: Props) {
  var _userCookieUtils$getU, _props$contextPanelPa;
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(props.open);
  const [selectedItemTooltipData, setSelectedItemTooltipData] = useState<ContextPanelData>(null);
  const eventsController = useEventsController();
  const cohortsStore = useCohorts();
  const dataGroupStore = useDataGroups();
  const currentProject = useCurrentProject();
  const behaviors = useBehaviors();
  const metrics = useMetrics();
  const i18n = useTranslation();
  const {
    t,
    print
  } = i18n;
  const userId = props.userId || ((_userCookieUtils$getU = userCookieUtils.getUserIdentityFromCookie()) === null || _userCookieUtils$getU === void 0 ? void 0 : _userCookieUtils$getU.id);
  useDomEvent(ref.current, "track", props.onTrack);
  useEffect(() => {
    const fetchCohorts = async () => {
      await cohortsStore.fetch({
        projectId: props.projectId,
        workspaceId: props.workspaceId
      });
    };
    fetchCohorts();
  }, [props.projectId, props.workspaceId, props.projectHasCohorts]);
  useEffect(() => {
    if (typeof eventsController.loading === "undefined") {
      eventsController.fetchData();
    }
  }, [eventsController]);
  useEffect(() => {
    dataGroupStore.fetch({
      projectId: props.projectId,
      workspaceId: props.workspaceId
    });
  }, [props.projectId, props.workspaceId]);
  useEffect(() => {
    userDetailsStore.fetch();
  }, []);
  const selectedLabel = useSelectedLabel({
    selected: props.selected,
    dataGroupId: props.dataGroupId
  });
  const label = useMemo(() => selectedLabel, [props.selected, props.dataGroupId, Object.keys(cohortsStore.value).length]);
  const isUnsaved = useMemo(() => {
    if (props.selected && isCohortMenuSectionItem(props.selected)) {
      const cohortItem = props.selected.value;
      return Boolean(typeof cohortItem.value !== "number" && !!cohortItem.value.cohort);
    }
    return false;
  }, [props.selected]);
  const dataGroupName = useMemo(() => {
    var _dataGroupStore$value;
    return (dataGroupStore === null || dataGroupStore === void 0 || (_dataGroupStore$value = dataGroupStore.value) === null || _dataGroupStore$value === void 0 || (_dataGroupStore$value = _dataGroupStore$value[props.dataGroupId]) === null || _dataGroupStore$value === void 0 ? void 0 : _dataGroupStore$value.displayName) || t("query-builder.dg-user");
  }, [props.dataGroupId]);

  // TODO - mixpanel.track
  const track = function track(eventName) {
    let extraProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let {
      cancelable = false
    } = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    const additionalProps = {
      ["usage context"]: TrackingUsageContext.Report
    };
    if (props.trackName) {
      additionalProps["tab"] = props.trackName;
    }
    emitTrackEvent(ref.current, {
      props: _objectSpread(_objectSpread({}, extraProps), additionalProps),
      eventName,
      cancelable
    });
  };
  const handleMenuChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      props.onOpen();
      track(TrackingEvent.OpenMetricsDropDown);
    } else {
      props.onClose();
    }
  };
  const handleShowContextPanel = () => {
    var _currentProject$get;
    const tooltipData = getTooltipData(props.selected, {
      readonly: props.readonly,
      dataGroupName,
      descriptionEditDisabled: props.descriptionEditDisabled,
      hideEditControls: props.hideEditControls,
      eventsController,
      cohortsStore,
      currentProject: (_currentProject$get = currentProject.get()) === null || _currentProject$get === void 0 ? void 0 : _currentProject$get.value,
      behaviors,
      metrics,
      i18n,
      dataGroups: Object.values(dataGroupStore.value)
    });
    setSelectedItemTooltipData(tooltipData);
  };
  const _handleInlineEvent = async (customEventId: null | number): Promise<void> => {
    var _props$selected;
    const {
      appliedValue,
      deletedId
    } = await promptCustomEvents({
      attrs: _objectSpread({
        open: true,
        'all-data-groups': props.allDataGroups,
        'project-has-people': props.projectHasPeople,
        'project-id': props.projectId,
        'workspace-id': props.workspaceId,
        'track-name': props.trackName
      }, customEventId ? {
        'selected-custom-event-id': customEventId
      } : {}),
      params: {
        dataGroupIds: convertDataGroupIdToSet(props.dataGroupId)
      }
    });
    track(TrackingEvent.OpenCustomEventsModal);
    if (appliedValue) {
      const item = getCustomEventMenuItem(appliedValue);
      const sectionItem: EventSectionItem = {
        icon: item.icon,
        label: item.label,
        menuType: MenuType.Event,
        value: item.value
      };
      // TODOCC add to cache...
      setOpen(false);
      props.onSelect(sectionItem);
    } else if (deletedId && ((_props$selected = props.selected) === null || _props$selected === void 0 || (_props$selected = _props$selected.value as EventItem) === null || _props$selected === void 0 ? void 0 : _props$selected.id) === deletedId) {
      props.onRemove();
    }
  };
  const _handleInlineCohort = async (cohortItem): Promise<void> => {
    let defaultCohort: CohortModel;
    if (cohortItem) {
      var _cohortItem$value, _cohortItem$value2;
      const cohortParsed = cohortItem.id ? cohortsStore.value[cohortItem.id] : ((_cohortItem$value = cohortItem.value) === null || _cohortItem$value === void 0 || (_cohortItem$value = _cohortItem$value.cohort) === null || _cohortItem$value === void 0 ? void 0 : _cohortItem$value.raw_cohort) || ((_cohortItem$value2 = cohortItem.value) === null || _cohortItem$value2 === void 0 ? void 0 : _cohortItem$value2.cohort);
      defaultCohort = CohortModel.parseCohort(cohortParsed);
    } else {
      defaultCohort = new CohortModel(_objectSpread(_objectSpread({}, new CohortModel()), {}, {
        dataGroupId: props.dataGroupId
      }));
    }
    const attrs = createInlineCohortModalAttrs({
      defaultCohort,
      dataGroupId: props.dataGroupId,
      showDataGroupsSelector: props.metricLevelDataGroups,
      userId,
      projectId: Number(props.projectId),
      workspaceId: Number(props.workspaceId),
      projectHasCohorts: props.projectHasCohorts,
      supportB2bCohorts: props.supportB2bCohorts,
      trackName: props.trackName
    });
    const {
      cohortStagedValue,
      cohortSavedValue
    } = await promptInlineCohorts(attrs);
    const cohortValue = cohortStagedValue || cohortSavedValue;
    track(TrackingEvent.OpenInlineCohortsModal);
    if (cohortValue) {
      const createdBy = {
        id: userId,
        name: userDetailsStore === null || userDetailsStore === void 0 ? void 0 : userDetailsStore.value.name,
        email: userDetailsStore === null || userDetailsStore === void 0 ? void 0 : userDetailsStore.value.email
      };
      const {
        commitedItem,
        isSaved,
        isEditingTemporaryCohort
      } = commitCohort(cohortsStore, createdBy, cohortValue);
      let itemValue: CohortItemValue;
      if (typeof commitedItem.value === "number") {
        const cohort = cohortsStore.value[commitedItem.value];
        // when saving an inline cohort, cohortStore.value contains both an inline cohort (type SerializedInlineCohort,
        // under the temporary id) and the saved version (type SerializedCohort, under the saved cohort id).
        // commitedItem.value is the saved cohort id, so this is the saved copy (SerializedCohort)
        const dataGroupId = (cohort as SerializedCohort).data_group_id;
        itemValue = _objectSpread(_objectSpread({}, dataGroupId ? {
          dataGroupId
        } : {}), {}, {
          resourceType: ResourceType.Cohort,
          label: print(commitedItem.label),
          value: commitedItem.value
        });
      } else {
        itemValue = {
          resourceType: ResourceType.Cohort,
          label: print(commitedItem.label),
          value: commitedItem.value as InlineCohortSegment
        };
      }
      const sectionItem: CohortSectionItem = {
        icon: "community",
        label: commitedItem.label,
        menuType: MenuType.Cohort,
        value: itemValue
      };
      if (isSaved) {
        track(TrackingEvent.SaveInlineCohorts);
      } else if (!isEditingTemporaryCohort) {
        track(TrackingEvent.CreateInlineCohort);
      } else {
        track(TrackingEvent.EditInlineCohorts);
      }

      // TODOCC add to cache...
      setOpen(false);
      props.onSelect(sectionItem);
    }
  };
  const contextPanelEventHandlers = createContextPanelEventHandlers({
    track,
    handleInlineEvent: customEventId => _handleInlineEvent(customEventId),
    handleInlineCohort: cohort => _handleInlineCohort(cohort),
    additionalArgs: {
      projectId: props.projectId,
      workspaceId: props.workspaceId
    },
    availableContexts: {
      behaviors,
      metrics
    }
  });
  return <NewQueryBlock ref={ref} label={label} buttonSize={props.buttonSize} className={clsx("type-multi-metrics", props.className)} dropMenuContainerClass='for-multi-metrics-menu' elref={props.elref} open={open} onChangeOpen={handleMenuChange} readonly={props.readonly} theme={ButtonTheme.QueryBuilder} selectorType={SelectorType.Button} leftIcon={props.leftIcon} isUnsaved={isUnsaved} onShowContextPanel={handleShowContextPanel} contextPanelElement={<ContextPanelReact positionStrategy={PositioningStrategy.Fixed} placement={Directions.Right} offset={8} showArrow elref={"contextPanel"} data={selectedItemTooltipData} positionParent={(_props$contextPanelPa = props.contextPanelParentElement) !== null && _props$contextPanelPa !== void 0 ? _props$contextPanelPa : ref.current} onTooltipContextAction={contextPanelEventHandlers.onTooltipContextAction} onTooltipContextEditDescription={contextPanelEventHandlers.onTooltipContextEditDescription} />} menuElement={open && <MultiMetricsMenu selected={props.selected} dataGroupName={dataGroupName} allDataGroups={props.allDataGroups} dataGroupId={props.dataGroupId} metricLevelDataGroups={props.metricLevelDataGroups} excludeAutotrackEvents={props.excludeAutotrackEvents} excludeCustomEvents={props.excludeCustomEvents} excludedMetricsRowTypes={props.excludedMetricsRowTypes} descriptionEditDisabled={props.descriptionEditDisabled} hideEditControls={props.hideEditControls} projectHasCohorts={props.projectHasCohorts} projectHasPeople={props.projectHasPeople} readonly={props.readonly} showAllEvents={props.showAllEvents} showAnyEvent={props.showAnyEvent} showSessionEvent={props.showSessionEvent} showSpecialDatasetProperty={props.showSpecialDatasetProperty} showTopEvents={props.showTopEvents} supportB2bCohorts={props.supportB2bCohorts} queryOrigin={props.queryOrigin} trackName={props.trackName} userId={userId} projectId={props.projectId} workspaceId={props.workspaceId} onUpdateInlineEvent={_handleInlineEvent} onUpdateInlineCohort={_handleInlineCohort} onTrack={props.onTrack} onSelect={selection => {
    setOpen(false);
    props.onSelect(selection);
  }} onCreate={props.onCreate} onDelete={props.onRemove} />} data-sentry-element="NewQueryBlock" data-sentry-component="MultiMetricsQueryBlock" data-sentry-source-file="query-block.tsx" />;
}